(ns client-portal.client.voice-commerce.orders.views
  (:require [client-portal.client.anonymous.data :as anon.d]
            [client-portal.client.layout :as l]
            [client-portal.client.voice-commerce.orders.components :as orders.c]
            [client-portal.client.voice-commerce.orders.data :as orders.d]
            [client-portal.client.voice-commerce.orders.drawer.views]
            [client-portal.client.voice-insights.empty-state :as empty-state]
            [shared.core.amelia-light.components.components :as alc]
            [shared.core.reactive :as re]
            [shared.core.style :as style]
            [shared.core.util :as util]))

(def ^:private total-column-width
  "Width of the Total column. The expanded panel reuses it so its Total lands in the
   same band as the cell above."
  90)

(def ^:private cell-padding-left
  "What `table-cell-style` insets a cell's content by — the panel's Total matches it."
  12)

(def ^:private columns
  "The table's columns, left to right: the header label, its width, and how the cell
   reads its value off an order row. `:money?` columns arrive as dollars (the query
   converts the cents) and only affect formatting — every column is left aligned.
   `:hide-with-panel?` columns drop out while the detail panel has the table squeezed,
   and a `:panel-flex` column takes the width they leave behind."
  [{:label "Order ID" :flex "0 0 130px" :panel-flex "1 0 130px" :key :order-id}
   {:label "Timestamp" :flex "0 0 130px" :key :occurred-at}
   {:label "Restaurant" :flex "1 0 160px" :key :restaurant-name :hide-with-panel? true}
   {:label "Guest" :flex "0 0 120px" :key :guest-name :hide-with-panel? true}
   {:label "Status" :flex "0 0 110px" :key :order-status}
   {:label "Customer Type" :flex "0 0 130px" :key :customer-type :hide-with-panel? true}
   {:label "Order Type" :flex "0 0 130px" :key :source :hide-with-panel? true}
   {:label "Items" :flex "0 0 70px" :key :items-count :numeric? true}
   {:label "Subtotal" :flex "0 0 90px" :key :subtotal :money? true}
   {:label "Total" :flex (str "0 0 " total-column-width "px") :key :total :money? true}])

(defn- visible-columns
  "`columns`, minus the ones that give up their space to the detail panel, with
   `:panel-flex` swapped in for whichever column absorbs it."
  [panel-open?]
  (cond->> columns
           panel-open?
           (remove :hide-with-panel?)

           panel-open?
           (mapv (fn [{:keys [panel-flex] :as column}]
                   (cond-> column
                           panel-flex
                           (assoc :flex panel-flex))))))

(defn- expand-toggle [{:keys [expanded? on-toggle]}]
  [alc/up-ghost-button {:icon-type (if expanded? :ChevronTopIcon :ChevronDownIcon)
                        :icon "md"
                        ;; The row opens the detail panel, so expanding must not also
                        ;; count as a row click.
                        :on-click (fn [e]
                                    (util/stop-event e)
                                    (on-toggle))
                        :style {:padding 8}}])

(def ^:private expand-column-width
  "Width of the trailing chevron column. Doubles as the detail panel's right padding
   so the breakdown ends where the Total cell above it does."
  45)

(defn details-panel [order]
  ;; Right padding is exactly the chevron column, so the last entry ends where the
  ;; Total cell above it does.
  (into [:div {:style {:background-color (style/alc-color "Background/page")
                       :flex 1
                       :display :flex
                       :justify-content :flex-end
                       :align-items :flex-start
                       :gap 32
                       :padding (str "16px " expand-column-width "px 16px 12px")}}]
        (mapv (fn [{:keys [label key column-aligned? bold?]}]
                [:div {:key label
                       :style (merge {:display :flex
                                      :flex-direction :column
                                      :gap 2}
                                     (if column-aligned?
                                       ;; The Total cell's own box: same basis, same inset.
                                       {:align-items :flex-start
                                        :flex (str "0 0 " total-column-width "px")
                                        :padding-left cell-padding-left}
                                       {:align-items :flex-end}))}
                 [:span {:style (cond-> {:color (style/alc-color "Text/secondary")
                                         :font-size 12
                                         :line-height 1.33
                                         :white-space :nowrap}
                                        bold? (assoc :font-weight style/font-semi-bold-weight))}
                  label]
                 ;; Matches a table cell: same size, same line height, and Total keeps
                 ;; the color it has in the row above.
                 [:span {:style (cond-> {:color (style/alc-color (if column-aligned?
                                                                   "Text/primary_header"
                                                                   "Text/primary"))
                                         :font-size 12
                                         :line-height 1.5}
                                        bold? (assoc :font-weight style/font-semi-bold-weight))}
                  (orders.c/money-label (get order key))]])
              [{:label "Subtotal" :key :subtotal}
               {:label "Tax" :key :tax}
               {:label "Delivery Fee" :key :delivery-fee}
               {:label "Incremental Order Fee" :key :sponsored}
               {:label "Base Commission" :key :base-commission}
               {:label "Total" :key :total :column-aligned? true :bold? true}])))

(defn- cell-label [{:keys [key numeric? money?]} v]
  (cond
    money? (orders.c/money-label v)
    (nil? v) "-"
    numeric? (util/readable-number v)
    (= key :restaurant-name) (anon.d/location-label v)
    :else (str v)))

(defn orders-header
  "The sticky page header. Reports its height so the table's sticky header can start
   below it instead of scrolling underneath."
  []
  (let [container-id "orders-header-container"
        orders-count- (re/subscribe [:voice-commerce/orders-count-])
        vs- (re/subscribe [:core/viewport-size])
        set-header-height #(when-let [h (some-> (js/document.getElementById container-id)
                                                (.getBoundingClientRect)
                                                (.-height))]
                             (re/dispatch [:voice-commerce/orders-set-header-height h]))]
    (re/create-class
      {:component-did-mount set-header-height
       :component-did-update set-header-height
       :reagent-render
       (fn []
         @vs-
         [:div {:id container-id
                :style {:background-color (style/alc-color "Background/card_hex")
                        :display :flex
                        :flex-direction :column
                        :gap 16
                        :padding "12px 16px 16px"
                        :position :sticky
                        :top 0
                        :left 0
                        :z-index (inc (style/z :medium))}}
          [:div {:style {:display :flex
                         :align-items :center
                         :justify-content :space-between}}
           [:span {:style {:color (style/alc-color "Text/primary_header")
                           :font-size 24
                           :font-weight 400
                           :line-height 1.33}}
            "Orders"
            (when-not (util/loading? @orders-count-)
              [:span {:style {:color (style/alc-color "Text/primary")
                              :margin-left 8}}
               (str "(" (some-> @orders-count- (util/readable-number)) ")")])]
           ;; Searching is just deep-linking: put the id in the URL and the panel the
           ;; route opens fetches it (the sessions convo-id search works the same way).
           (let [search-order-id (fn [e]
                                   (when-let [el (js/document.getElementById "order-id-search-input")]
                                     (let [v (or (some-> e
                                                         (.-clipboardData)
                                                         (.getData "text/plain"))
                                                 (.-value el))]
                                       (re/dispatch [:router/go-with-merged-query-params
                                                     {:order (orders.d/order->order-qp {:order-id v})}])
                                       (set! (.-value el) "")
                                       (.blur el))))]
             [alc/up-input {:id :order-id-search-input
                            :class-name "w-60"
                            :placeholder "Search By Order ID"
                            :on-key-down #(when (util/enter-key-event? %)
                                            (search-order-id %))
                            :on-paste search-order-id}])]])})))

(defn orders-list []
  (let [orders- (re/subscribe [:voice-commerce/orders-list-flattened-])
        loading?- (re/subscribe [:voice-commerce/orders-list-loading?-])
        has-more?- (re/subscribe [:voice-commerce/orders-list-data-has-more?-])
        next-page-loading?- (re/subscribe [:voice-commerce/orders-list-next-page-loading?-])
        next-page- (re/subscribe [:voice-commerce/orders-list-next-page-])
        header-height- (re/subscribe [:voice-commerce/orders-header-height-])
        selected-order- (re/subscribe [:voice-commerce/selected-order-])
        expanded* (re/ratom #{})]
    (fn []
      [alc/table {:no-data-text "No orders to display"
                  :header-top @header-height-
                  :loading? @loading?-
                  :loading-more? @next-page-loading?-
                  :scroll-container-id l/core-scroll-content-id
                  :bottom-px-buffer 200
                  :on-bottom-buffer (when @has-more?-
                                      #(re/dispatch [:voice-commerce/orders-load-list-data
                                                     [nil {:page @next-page-}]]))
                  :has-more-rows? true
                  :sticky-headers? true
                  :sticky-header-x-scroll? true
                  :no-data-material-icon :inventory_2
                  :row-style {:height :auto
                              :align-items :center}
                  :flex-vals (conj (mapv :flex (visible-columns @selected-order-))
                                   (str "0 0 " expand-column-width "px"))
                  :headers (conj (mapv :label (visible-columns @selected-order-)) "")
                  ;; An expanded order adds a row beneath its own: a single cell, which
                  ;; the table lays out full width since its count can't match flex-vals.
                  :rows (into []
                              (comp (map-indexed vector)
                                    (mapcat (fn [[idx order]]
                                              (let [row-key (or (:order-id order) idx)
                                                    expanded? (contains? @expanded* row-key)
                                                    selected? (some-> (:order-id order) (= (:order-id @selected-order-)))
                                                    ;; Selected (panel open) outranks expanded; the panel row
                                                    ;; below keeps the plain background either way.
                                                    row-background (cond
                                                                     selected? (style/alc-color "Background/active_secondary")
                                                                     expanded? (style/alc-color "Background/hover")
                                                                     :else nil)
                                                    order-row {:key (str row-key)
                                                               :style (when row-background
                                                                        {:background-color row-background})
                                                               :hover-style (when row-background
                                                                              {:background-color row-background})
                                                               ;; No `:open?` — the event toggles, as the
                                                               ;; sessions list row does.
                                                               :on-click #(re/dispatch
                                                                            [:voice-commerce/orders-toggle-drawer-open
                                                                             {:order-id (:order-id order)
                                                                              :order-idx idx}])
                                                               :cells (conj (mapv (fn [{:keys [key] :as column}]
                                                                                    (let [v (get order key)
                                                                                          label (cell-label column v)]
                                                                                      (case key
                                                                                        :order-id {:label label
                                                                                                   :copyable? true
                                                                                                   :style {:color (style/alc-color "Text/secondary")
                                                                                                           :white-space :nowrap}}
                                                                                        :occurred-at {:label [:span {:style {:color (style/alc-color "Text/secondary")}}
                                                                                                              label]}
                                                                                        :order-status {:label [orders.c/status-cell v]}
                                                                                        :customer-type {:label [orders.c/source-cell v]}
                                                                                        :source {:label [orders.c/source-cell v]}
                                                                                        :total {:label label
                                                                                                :style {:color (style/alc-color "Text/primary_header")}}
                                                                                        {:label label})))
                                                                                  (visible-columns @selected-order-))
                                                                            {:label [:div {:style {:display :flex
                                                                                                   :align-items :center
                                                                                                   :width "100%"}}
                                                                                     [expand-toggle
                                                                                      {:expanded? expanded?
                                                                                       :on-toggle #(swap! expanded*
                                                                                                          (fn [ks]
                                                                                                            (if (contains? ks row-key)
                                                                                                              (disj ks row-key)
                                                                                                              (conj ks row-key))))}]]
                                                                             :style {:display :flex
                                                                                     :justify-content :flex-end
                                                                                     :padding 0}})}]
                                                (cond-> [order-row]
                                                        expanded?
                                                        (conj {:key (str row-key "-details")
                                                               :style {:padding 0}
                                                               :cells [{:label [details-panel order]
                                                                        :style {:padding 0}}]}))))))
                              @orders-)}])))

(defn root [_]
  (let [orders- (re/subscribe [:voice-commerce/orders-list-flattened-])
        loading?- (re/subscribe [:voice-commerce/orders-list-loading?-])
        ;; Reopen the panel the URL names, so a refresh or a shared link lands on
        ;; the same order (the sessions root does this with its `conv` param).
        ;; Only a URL that just lost its order clears the selection — a row click selects
        ;; before its `order` param lands, and a re-render in between mustn't undo it.
        sync-order-from-qp (fn [this prev-order-qp]
                             (if-let [order (some-> (:order (re/props this))
                                                    (orders.d/order-qp->order))]
                               (re/dispatch [:voice-commerce/orders-toggle-drawer-open
                                             (assoc order :open? true)])
                               (when (not= prev-order-qp (:order (re/props this)))
                                 (re/dispatch [:voice-commerce/orders-clear-selected]))))]
    (re/create-class
      {:component-did-mount (fn [this]
                              (sync-order-from-qp this ::mount)
                              (re/dispatch [:voice-commerce/orders-initialize]))
       :component-did-update (fn [this [_ prev-args]]
                               (sync-order-from-qp this (:order prev-args)))
       :reagent-render
       (fn [args]
         [l/logged-in-with-header-nav-voice-commerce
          (merge args {:concept-dropdown? true
                       :location-dropdown? true})
          [:div {:style {:display :flex
                         :flex-direction :column
                         :width "100%"
                         :min-height "100%"}}
           [orders-header]
           [:div {:style {:flex 1
                          :display :flex
                          :flex-direction :column
                          :padding "0 16px 12px"}}
            (cond
              @loading?- [empty-state/loading-orders-card]
              (empty? @orders-) [empty-state/no-orders-card]
              :else [orders-list])]]])})))
